import type {
  Actor,
  Command,
  CommandEnvelope,
  CommandRejection,
  CommandResult,
  Ids,
  ProjectEventEnvelope,
  ProjectState,
} from "@minga/core"
import { commit, evolve, mintId } from "@minga/core"
import type { CommandId, PresenceFrame, RunActivity, SyncFrame } from "@minga/protocol"

/**
 * The live project store (spec §7.6 step 2, decision 0017). Pure and synchronous: the sync client (sync.ts) feeds it
 * frames and command results, the views read it through `useSyncExternalStore`.
 *
 * - `confirmed` is the server's state: a `Snapshot`, then whole-command `Events` batches folded with core `evolve`.
 *   Batches are deduped by `seq`; a gap asks the sync client to resubscribe from scratch (`fromSeq: null`).
 * - `pending` are commands this tab sent and the server has not confirmed yet. `view` = `pending` folded over
 *   `confirmed` with core `commit` as the viewer, so a write shows at once and is replaced by the server's events.
 * - A pending command is dropped when an `Events` batch carries its `commandId`, when the confirmed `seq` reaches the
 *   seq its result named, or when it is rejected (then it moves to `rejections`, shown inline).
 * - A send that failed in transport (socket closed before the result came back) is kept, marked `unconfirmed`: the
 *   server may have applied it. The sync client re-sends the SAME envelope once the connection is live again, and the
 *   server's commandId dedupe answers `Deduplicated` if it was applied. Only when that gives up does it move to
 *   `rejections`; an `Events` batch that later carries its `commandId` clears that entry again.
 * - `activity` is the ephemeral `RunActivity` per run (spec §4.3, §7.2): `Presence{runs, runsRemoved}` deltas upsert and
 *   remove it (their `people`/`agents` are ignored); it never enters `ProjectState`. An entry is pruned 10 min after
 *   its run ended. Run milestones come from the durable `Run.lastMilestone`, so nothing else is kept in memory.
 */

export type SyncFrameValue = typeof SyncFrame.Type
export type ConnectionStatus = "connecting" | "live" | "reconnecting"

export interface PendingCommand {
  readonly envelope: CommandEnvelope
  readonly submittedAt: number
  /** Set from an Applied/Proposed/Deduplicated result: the command is confirmed once `confirmed.seq` reaches it. */
  readonly awaitSeq: number | null
  /** The last send failed in transport: the server may or may not have applied it. It is re-sent when live again. */
  readonly unconfirmed: boolean
}

export interface RejectedCommand {
  readonly commandId: string
  readonly command: Command
  readonly rejection: CommandRejection | null
  /** A transport failure ("not delivered"), when there is no typed rejection. */
  readonly error: string | null
  readonly at: number
}

export interface LiveSnapshot {
  readonly status: ConnectionStatus
  readonly confirmed: ProjectState | null
  readonly pending: ReadonlyArray<PendingCommand>
  readonly rejections: ReadonlyArray<RejectedCommand>
  /** The optimistic state the views render: `confirmed` plus `pending`, folded as the viewer. */
  readonly view: ProjectState | null
  /** Ephemeral run activity by runId (Presence deltas). */
  readonly activity: ReadonlyMap<string, RunActivity>
  /**
   * Decision 0051: the seq of the newest confirmed `CommentPosted` of kind `update` or `decision`, or `CommentEdited`
   * (a snapshot sets it to its seq). The Overview's comment reads refetch when it moves; optimistic events never move it.
   */
  readonly commentsSeq: number
  /** Decision 0051: the seq of the newest confirmed brief change (`BriefRevised`, an old `ProjectUpdated{description}`). */
  readonly briefSeq: number
}

export type FrameOutcome = "applied" | "ignored" | "resync"

export interface StoreDeps {
  readonly userId: string
  readonly now?: () => number
  readonly random10?: () => Uint8Array
}

const MAX_REJECTIONS = 20
/** An ended run's activity stays this long (the Activity sheet's "Recent" and the 2 min linger in the team list). */
export const ACTIVITY_RETENTION_MS = 10 * 60_000

const defaultRandom = (): Uint8Array => {
  const bytes = new Uint8Array(10)
  globalThis.crypto.getRandomValues(bytes)
  return bytes
}

type Listener = () => void

export class ProjectStore {
  readonly viewer: Actor
  private readonly now: () => number
  private readonly random10: () => Uint8Array
  private readonly listeners = new Set<Listener>()
  private confirmed: ProjectState | null = null
  private pending: PendingCommand[] = []
  private rejections: RejectedCommand[] = []
  private activity = new Map<string, RunActivity>()
  private status: ConnectionStatus = "connecting"
  private commentsSeq = 0
  private briefSeq = 0
  /** Set by `Resync` or a detected gap: the next subscription asks for a snapshot. */
  private needsSnapshot = true
  /** Counts the times the connection became live; a failed send waits for it to move before re-sending. */
  private liveCount = 0
  private snapshot: LiveSnapshot

  constructor(deps: StoreDeps) {
    this.viewer = { _tag: "user", userId: deps.userId as Ids["UserId"] }
    this.now = deps.now ?? Date.now
    this.random10 = deps.random10 ?? defaultRandom
    this.snapshot = this.build()
  }

  // ---- reading ----------------------------------------------------------------------------------------------

  getSnapshot = (): LiveSnapshot => this.snapshot

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /** How many times the connection has become live (see `unconfirm`). */
  liveEpoch(): number {
    return this.liveCount
  }

  /** The `fromSeq` for the next `Subscribe`: null asks for a snapshot. */
  resumeFrom(): Ids["Seq"] | null {
    if (this.needsSnapshot || !this.confirmed) return null
    return this.confirmed.seq
  }

  // ---- the sync client's inputs --------------------------------------------------------------------------------

  setStatus(status: ConnectionStatus): void {
    if (status === this.status) return
    this.setStatusQuietly(status)
    this.changed()
  }

  private setStatusQuietly(status: ConnectionStatus): void {
    if (status === this.status) return
    this.status = status
    if (status === "live") this.liveCount += 1
  }

  /** Apply one frame. "resync" tells the sync client to end this subscription and resubscribe with `fromSeq: null`. */
  applyFrame(frame: SyncFrameValue): FrameOutcome {
    switch (frame._tag) {
      case "Snapshot": {
        // A snapshot at or behind what we have adds nothing (a duplicate answer to a slow resubscribe).
        if (this.confirmed && !this.needsSnapshot && frame.state.seq <= this.confirmed.seq) return "ignored"
        this.confirmed = frame.state
        this.needsSnapshot = false
        // what the Overview read before may be stale after a snapshot: both reads refetch
        this.commentsSeq = frame.state.seq
        this.briefSeq = frame.state.seq
        this.setStatusQuietly("live")
        this.settleBySeq()
        this.pruneActivity()
        this.changed()
        return "applied"
      }
      case "Events":
        return this.applyEvents(frame.events)
      case "Resync":
        this.needsSnapshot = true
        return "resync"
      case "Presence":
        return this.applyPresence(frame as PresenceFrame)
    }
  }

  /**
   * Decision 0151: the first frames fetched over HTTP (`GET …/snapshot`) while the socket opens. Taken only while the
   * store has no state yet; the connection's status is left alone (the socket is not live), and the socket then
   * subscribes from this snapshot's seq. True when taken.
   */
  applyEarly(frames: ReadonlyArray<SyncFrameValue>): boolean {
    if (this.confirmed) return false
    let taken = false
    for (const frame of frames) {
      if (frame._tag === "Snapshot" && !this.confirmed) {
        this.confirmed = frame.state
        this.needsSnapshot = false
        this.commentsSeq = frame.state.seq
        this.briefSeq = frame.state.seq
        this.settleBySeq()
        this.pruneActivity()
        this.changed()
        taken = true
      } else if (frame._tag === "Presence" && taken) this.applyPresence(frame as PresenceFrame)
    }
    return taken
  }

  /**
   * A Presence frame with `runs`/`runsRemoved` is a delta for runs only (its `people`/`agents` are empty and ignored).
   * Without them it carries nothing the web uses (agent presence is derived from the state with core agentPresence).
   * It may arrive before the first Snapshot: the activity is kept and shown once the state is there.
   */
  private applyPresence(frame: PresenceFrame): FrameOutcome {
    if (frame.runs === undefined && frame.runsRemoved === undefined) return "ignored"
    const next = new Map(this.activity)
    for (const runId of frame.runsRemoved ?? []) next.delete(runId)
    for (const a of frame.runs ?? []) {
      const prev = next.get(a.runId)
      // Newest per run: an older epoch, or an older frame of the same epoch, never replaces a newer one.
      if (prev && (prev.epoch > a.epoch || (prev.epoch === a.epoch && prev.at > a.at))) continue
      next.set(a.runId, a)
    }
    this.activity = next
    this.pruneActivity()
    this.changed()
    return "applied"
  }

  /** Drop the activity of runs that ended more than {@link ACTIVITY_RETENTION_MS} ago. */
  private pruneActivity(): void {
    const now = this.now()
    let next: Map<string, RunActivity> | null = null
    for (const [runId, a] of this.activity) {
      const run = this.confirmed?.runs.find((r) => r.id === runId)
      const endedAt = run?.state._tag === "ended" ? (run.endedAt ?? a.at) : a.phase === "ended" ? a.at : null
      if (endedAt !== null && now - endedAt > ACTIVITY_RETENTION_MS) {
        next ??= new Map(this.activity)
        next.delete(runId)
      }
    }
    if (next) this.activity = next
  }

  private applyEvents(events: ReadonlyArray<ProjectEventEnvelope>): FrameOutcome {
    const base = this.confirmed
    if (!base || this.needsSnapshot) {
      this.needsSnapshot = true
      return "resync"
    }
    const fresh = [...events].sort((a, b) => a.seq - b.seq).filter((e) => e.seq > base.seq)
    if (fresh.length === 0) return "ignored"
    let state = base
    const commandIds = new Set<string>()
    for (const envelope of fresh) {
      if (envelope.seq !== state.seq + 1) {
        // A gap: what we hold can't be extended. Keep the confirmed state and ask for a snapshot.
        this.needsSnapshot = true
        if (state !== base) {
          this.confirmed = state
          this.changed()
        }
        return "resync"
      }
      const { event, ...meta } = envelope
      state = evolve(state, event, meta)
      if (envelope.commandId) commandIds.add(envelope.commandId)
      this.noteReads(envelope)
    }
    this.confirmed = state
    this.setStatusQuietly("live")
    this.pending = this.pending.filter((p) => !commandIds.has(p.envelope.commandId))
    // A command reported as not delivered that the server did apply after all: its events are the proof.
    this.rejections = this.rejections.filter((r) => r.rejection !== null || !commandIds.has(r.commandId))
    this.settleBySeq()
    this.pruneActivity()
    this.changed()
    return "applied"
  }

  /** Move `commentsSeq` / `briefSeq` for a confirmed event the Overview's reads depend on (decision 0051). */
  private noteReads(envelope: ProjectEventEnvelope): void {
    const e = envelope.event
    if (
      (e._tag === "CommentPosted" && (e.comment.kind === "update" || e.comment.kind === "decision")) ||
      e._tag === "CommentEdited"
    )
      this.commentsSeq = envelope.seq
    else if (e._tag === "BriefRevised" || (e._tag === "ProjectUpdated" && "description" in e.patch))
      this.briefSeq = envelope.seq
  }

  /** Drop pending commands whose result named a seq that the confirmed state has reached. */
  private settleBySeq(): void {
    const seq = this.confirmed?.seq ?? -1
    this.pending = this.pending.filter((p) => p.awaitSeq === null || p.awaitSeq > seq)
  }

  // ---- writes ---------------------------------------------------------------------------------------------------

  /** Wrap a command in an envelope with a fresh client-minted `commandId` and add it to `pending`. */
  propose(command: Command): CommandEnvelope {
    const envelope: CommandEnvelope = {
      commandId: mintId("cmd", this.now(), this.random10()) as typeof CommandId.Type,
      baseSeq: (this.confirmed?.seq ?? 0) as Ids["Seq"],
      command,
    }
    this.pending = [
      ...this.pending,
      { envelope, submittedAt: this.now(), awaitSeq: null, unconfirmed: false },
    ]
    this.changed()
    return envelope
  }

  /** The server's answer to `Dispatch`. */
  settle(commandId: string, result: CommandResult): void {
    const entry = this.pending.find((p) => p.envelope.commandId === commandId)
    if (!entry) return
    if (result._tag === "Rejected") {
      this.reject(entry, result.rejection, null)
      return
    }
    const original = result._tag === "Deduplicated" ? result.original : result
    const awaitSeq = original._tag === "Applied" ? original.lastSeq : original.seq
    this.pending = this.pending.map((p) => (p === entry ? { ...p, awaitSeq, unconfirmed: false } : p))
    this.settleBySeq()
    this.changed()
  }

  /**
   * A send failed in transport (socket closed before the result, reconnecting). The command stays pending and in the
   * view; the sync client re-sends the same envelope once live again. False when the command is no longer pending.
   */
  unconfirm(commandId: string): boolean {
    const entry = this.pending.find((p) => p.envelope.commandId === commandId)
    if (!entry) return false
    if (!entry.unconfirmed) {
      this.pending = this.pending.map((p) => (p === entry ? { ...p, unconfirmed: true } : p))
      this.changed()
    }
    return true
  }

  /** The command could not be delivered after re-sending for a while. It is dropped and shown as failed. */
  fail(commandId: string, error: string): void {
    const entry = this.pending.find((p) => p.envelope.commandId === commandId)
    if (entry) this.reject(entry, null, error)
  }

  dismissRejection(commandId: string): void {
    const next = this.rejections.filter((r) => r.commandId !== commandId)
    if (next.length === this.rejections.length) return
    this.rejections = next
    this.changed()
  }

  private reject(entry: PendingCommand, rejection: CommandRejection | null, error: string | null): void {
    this.pending = this.pending.filter((p) => p !== entry)
    this.rejections = [
      ...this.rejections,
      {
        commandId: entry.envelope.commandId,
        command: entry.envelope.command,
        rejection,
        error,
        at: this.now(),
      },
    ].slice(-MAX_REJECTIONS)
    this.changed()
  }

  // ---- derived ---------------------------------------------------------------------------------------------------

  private build(): LiveSnapshot {
    return {
      status: this.status,
      confirmed: this.confirmed,
      pending: this.pending,
      rejections: this.rejections,
      view: this.fold(),
      activity: this.activity,
      commentsSeq: this.commentsSeq,
      briefSeq: this.briefSeq,
    }
  }

  /** `pending` folded over `confirmed` with core decide + evolve as the viewer. A locally rejected one is skipped. */
  private fold(): ProjectState | null {
    if (!this.confirmed) return null
    let state = this.confirmed
    for (const p of this.pending) {
      const out = commit(state, p.envelope, {
        actor: this.viewer,
        now: this.now(),
        correlationId: `optimistic:${p.envelope.commandId}`,
      })
      if (out.result._tag !== "Rejected") state = out.state
    }
    return state
  }

  private changed(): void {
    this.snapshot = this.build()
    for (const listener of this.listeners) listener()
  }
}
